// Shared metadata, routing and status styling for global search.
// Used by both the header dropdown (app-global-search) and the results page
// (SearchResultsPage) so they stay visually and behaviourally identical.

import { GlobalSearchEntityType, GlobalSearchResultItem } from "src/app/interfaces/entities/search";
import { IconName } from "src/app/helpers/icon-exports";
import { PillStatusConfig } from "../status-pill/status-pill.component";
import { StatusData } from "src/app/constants/Status";
import { AppGlobals } from "src/app/app.globals";

export interface GroupMeta {
  type: GlobalSearchEntityType;
  label: string;
  icon: IconName;
  chipBg: string;
  iconColor: string;
  featureFlag?: string; // FeatureFlags value — undefined means always visible
}

// Group metadata in display order. The order deliberately follows SEARCH_WAVES: a group
// from a later wave must never render above one already on screen, or the slow task wave
// would land at the top and shove everything the user is reading down the page.
export const GROUP_META: GroupMeta[] = [
  { type: "Company", label: $localize`:@@searchGroupCompanies:Companies`, icon: "building", chipBg: "rgba(122, 92, 207, 0.25)", iconColor: "#7a5ccf" },
  { type: "MaStR", label: $localize`:@@searchGroupMaStR:MaStR`, icon: "database", chipBg: "rgba(0,87,99,0.2)", iconColor: "#005763" },
  { type: "Person", label: $localize`:@@searchGroupPersons:Persons`, icon: "user-multiple", chipBg: "rgba(122, 92, 207, 0.25)", iconColor: "#7a5ccf" },
  { type: "Project", label: $localize`:@@searchGroupProjects:Projects`, icon: "solar-panel", chipBg: "#deefd2", iconColor: "#629d37" },
  { type: "Site", label: $localize`:@@searchGroupSites:Sites`, icon: "location", chipBg: "rgba(37, 99, 235, 0.25)", iconColor: "#2563eb" },
  { type: "Contract", label: $localize`:@@searchGroupContracts:Contracts`, icon: "contracts", chipBg: "#fee3c2", iconColor: "#e8833a", featureFlag: "Contracts" },
  { type: "GalleryImage", label: $localize`:@@searchGroupGalleryImages:Images`, icon: "image", chipBg: "rgba(234,179,8,0.2)", iconColor: "#ca8a04" },
  { type: "Document", label: $localize`:@@searchGroupDocuments:Documents`, icon: "document", chipBg: "rgba(58, 175, 169, 0.25)", iconColor: "#3aafa9" },
  { type: "SandboxDocument", label: $localize`:@@searchGroupSandbox:Sandbox`, icon: "folder", chipBg: "rgba(234,88,12,0.2)", iconColor: "#ea580c", featureFlag: "Sandbox" },
  { type: "Property", label: $localize`:@@searchGroupProperties:Properties`, icon: "maps", chipBg: "#deefd2", iconColor: "#629d37" },
  { type: "TechComponent", label: $localize`:@@searchGroupComponents:Components`, icon: "layers", chipBg: "rgba(58, 175, 169, 0.25)", iconColor: "#3aafa9" },
  { type: "ProjectCost", label: $localize`:@@searchGroupProjectCosts:Project Costs`, icon: "coins-euro", chipBg: "rgba(14,165,233,0.2)", iconColor: "#0ea5e9", featureFlag: "ProjectCost" },
  { type: "Spark", label: $localize`:@@searchGroupSparks:Project Hub`, icon: "comment", chipBg: "rgba(236,72,153,0.25)", iconColor: "#db2777" },
  { type: "UserTask", label: $localize`:@@searchGroupUserTasks:Tasks`, icon: "task-done", chipBg: "rgba(99,102,241,0.25)", iconColor: "#6366f1" },
  { type: "Microtask", label: $localize`:@@searchGroupMicrotasks:Microtasks`, icon: "check-list", chipBg: "rgba(220,38,38,0.25)", iconColor: "#dc2626" },
];

// Returns only the groups the current tenant has access to (feature-flag gated).
export function enabledGroupMeta(): GroupMeta[] {
  return GROUP_META.filter((m) => !m.featureFlag || AppGlobals.UserCan(m.featureFlag));
}

export type SearchScope = "global" | "site" | "project";

// The types a given scope may ask for. The backend is the only place scope is applied.
// Contacts and contracts have no project column of their own but are still reachable —
// the backend narrows them through ProjectContacts, contact-typed task results and
// ContractLink, the same links the Kontakte and Verträge tabs are built from. Only a site
// is genuinely not something a project contains.
export function typesForScope(scope: SearchScope): GlobalSearchEntityType[] {
  const all = enabledGroupMeta().map((m) => m.type);
  return scope === "project" ? all.filter((t) => t !== "Site") : all;
}

// Search waves, cheapest first, so the dropdown paints something long before the slow
// joins finish. Wave 1 is small indexed tables, wave 2 mid-size joins, wave 3 the task
// hierarchy and its comment scans. SharePoint is a separate call and lands whenever it does
// — but Document/SandboxDocument appear here too, because the DB-tracked contract, property
// and component files only come back from this endpoint.
// Every enabled type must appear in exactly one wave, or it is silently never searched.
export const SEARCH_WAVES: GlobalSearchEntityType[][] = [
  ["Person", "Company", "Contract", "Project", "Site", "MaStR"],
  ["Document", "SandboxDocument", "Property", "TechComponent", "ProjectCost", "GalleryImage", "Spark"],
  ["UserTask", "Microtask"],
];

// Splits the wave plan down to the types this scope actually asks for, dropping any wave
// that ends up empty.
export function wavesForScope(scope: SearchScope): GlobalSearchEntityType[][] {
  const allowed = new Set(typesForScope(scope));
  return SEARCH_WAVES.map((w) => w.filter((t) => allowed.has(t))).filter((w) => w.length > 0);
}

export function metaFor(type: GlobalSearchEntityType): GroupMeta | undefined {
  return GROUP_META.find((m) => m.type === type);
}

// The backend serialises the Counts dictionary with camelCase keys (e.g. "techComponent"),
// while result.entityType and our metadata use PascalCase ("TechComponent"). Normalise the
// raw counts to canonical PascalCase keys so lookups by EntityType work regardless of casing.
// Only enabled (feature-flagged) types are included.
export function normalizeCounts(
  raw: { [k: string]: number } | null | undefined,
  only?: GlobalSearchEntityType[]
): { [type: string]: number } {
  const out: { [type: string]: number } = {};
  if (!raw) return out;
  // `only` keeps a per-wave response from zeroing the counts an earlier wave already set.
  const wanted = only ? new Set(only) : null;
  for (const meta of enabledGroupMeta()) {
    if (wanted && !wanted.has(meta.type)) continue;
    const camel = meta.type.charAt(0).toLowerCase() + meta.type.slice(1);
    out[meta.type] = raw[meta.type] ?? raw[camel] ?? 0;
  }
  return out;
}

// ---- Handoff between the header dropdown and the results page ----
// Both run the same query, and "Alle Ergebnisse anzeigen" used to re-run it from scratch.
// The dropdown parks its finished set here; the results page consumes it once and paints
// immediately. ponytail: module-level, not a service — one SPA session, one live search.
interface ParkedSearch {
  key: string;
  results: GlobalSearchResultItem[];
  counts: { [type: string]: number };
}
let parkedSearch: ParkedSearch | null = null;

export function searchCacheKey(q: string, siteId: number | null, projectId: number | null): string {
  return `${q}|${siteId ?? ""}|${projectId ?? ""}`;
}

export function parkSearch(key: string, results: GlobalSearchResultItem[], counts: { [type: string]: number }): void {
  parkedSearch = { key, results: [...results], counts: { ...counts } };
}

// Consumes the parked set: a second navigation to the same query refetches, so results
// can never be served from an arbitrarily old cache.
export function takeParkedSearch(key: string): ParkedSearch | null {
  if (parkedSearch?.key !== key) return null;
  const hit = parkedSearch;
  parkedSearch = null;
  return hit;
}

// Merges SharePoint search hits into an existing result set, deduplicating by title+subtitle
// against DB-tracked Document rows (which have fileOpenId set, SP rows have fileOpenIdStr).
export function mergeSharePointResults(
  current: GlobalSearchResultItem[],
  spItems: GlobalSearchResultItem[]
): GlobalSearchResultItem[] {
  const dbDocKey = (r: GlobalSearchResultItem) => `${r.title}\0${r.subtitle ?? ''}`;
  const dbDocKeys = new Set(
    current.filter(r => (r.entityType === 'Document' || r.entityType === 'SandboxDocument') && r.fileOpenIdStr == null).map(dbDocKey)
  );
  const fresh = spItems.filter(r =>
    (r.entityType !== 'Document' && r.entityType !== 'SandboxDocument') || !dbDocKeys.has(dbDocKey(r))
  );
  return [...current, ...fresh];
}

export interface SearchNavTarget {
  commands: unknown[];
  queryParams?: { [k: string]: unknown };
}

// Maps a result to its Angular navigation target. Tech Components open the global
// components page focused on the component (?componentId=). Properties have no detail
// route, so they open the containing site's Properties tab.
export function routeForResult(item: GlobalSearchResultItem): SearchNavTarget | null {
  switch (item.entityType) {
    case "Project":
      return item.siteId
        ? { commands: ["/site", item.siteId, "project", item.id, "projectInfo", "overview"] }
        : null;
    case "Site":
      return { commands: ["/site", item.id, "site-overview"] };
    case "Company":
      return { commands: ["/contacts", "company", item.id] };
    case "Person":
      return { commands: ["/contacts", "person", item.id] };
    case "Contract":
      return { commands: ["/contracts", "edit", item.id] };
    case "Property":
      return item.siteId
        ? { commands: ["/site", item.siteId, "properties"], queryParams: { propertyId: item.id } }
        : null;
    case "TechComponent":
      return { commands: ["/tech-components"], queryParams: { componentId: item.id } };
    case "Document":
    case "SandboxDocument":
      return null; // open in FileViewer (handled by the caller)
    case "Microtask":
      return item.siteId && item.projectId
        ? { commands: ["/site", item.siteId, "project", item.projectId, "microtasks"] }
        : null;
    case "UserTask":
      return item.siteId && item.projectId
        ? { commands: ["/site", item.siteId, "project", item.projectId, "tasks", item.id] }
        : null;
    case "Spark":
      return item.siteId && item.projectId && item.fileOpenId
        ? { commands: ["/site", item.siteId, "project", item.projectId, "project-hub", item.fileOpenId] }
        : null;
    case "MaStR":
      return item.siteId && item.projectId
        ? { commands: ["/site", item.siteId, "project", item.projectId, "projectInfo", "mastr"] }
        : null;
    case "GalleryImage":
      return item.siteId && item.id
        ? { commands: ["/site", item.siteId, "project", item.id, "gallery"] }
        : null;
    case "ProjectCost":
      return item.siteId && item.projectId
        ? { commands: ["/site", item.siteId, "project", item.projectId, "project-costs", "actual"], queryParams: { costId: item.id } }
        : null;
    default:
      return null;
  }
}

// ---- Status badge styling (reuses app-status-pill) ----

// Light pill palette (bg / text / border) per colour family — matches the "OFFEN"
// and Figma search badge style (light fill, coloured text + border).
const PILL_PALETTE: Record<string, { bg: string; text: string; border: string }> = {
  blue: { bg: "#d9f5fd", text: "#005ab5", border: "#45c1eb" },
  green: { bg: "#deefd2", text: "#4f7d2c", border: "#629d37" },
  red: { bg: "#fde2e0", text: "#b32218", border: "#e8675b" },
  orange: { bg: "#fee3c2", text: "#9a5a12", border: "#e8833a" },
  grey: { bg: "#f0f0f0", text: "#555555", border: "#c4c4c4" },
};

// Status text -> colour family. Follows the app's existing convention
// (project-info-overview: Aktiv=blue, Abgeschlossen=green, Abgebrochen=red, Inaktiv=orange),
// with common synonyms; anything unknown falls back to grey.
const STATUS_FAMILY: Record<string, string> = {
  aktiv: "blue", active: "blue", offen: "blue", open: "blue", "in bearbeitung": "blue",
  abgeschlossen: "green", completed: "green", erledigt: "green", fertig: "green", done: "green",
  abgebrochen: "red", aborted: "red", storniert: "red", cancelled: "red", canceled: "red",
  inaktiv: "orange", inactive: "orange", pausiert: "orange",
};

// Normalises raw (sometimes English) status values from the DB to a display
// label. Display-only — the stored data is unchanged. Unknown values keep their raw text.
const STATUS_LABEL: Record<string, string> = {
  aktiv: $localize`:@@statusLabelActive:Active`, active: $localize`:@@statusLabelActive:Active`,
  inaktiv: $localize`:@@statusLabelInactive:Inactive`, inactive: $localize`:@@statusLabelInactive:Inactive`,
  offen: $localize`:@@statusLabelOpen:Open`, open: $localize`:@@statusLabelOpen:Open`,
  "in bearbeitung": $localize`:@@statusLabelInProgress:In Progress`,
  abgeschlossen: $localize`:@@statusLabelCompleted:Completed`, completed: $localize`:@@statusLabelCompleted:Completed`,
  erledigt: $localize`:@@statusLabelDone:Done`, fertig: $localize`:@@statusLabelFinished:Finished`, done: $localize`:@@statusLabelDone:Done`,
  abgebrochen: $localize`:@@statusLabelCancelled:Cancelled`, aborted: $localize`:@@statusLabelCancelled:Cancelled`,
  storniert: $localize`:@@statusLabelCancelled:Cancelled`, cancelled: $localize`:@@statusLabelCancelled:Cancelled`, canceled: $localize`:@@statusLabelCancelled:Cancelled`,
  pausiert: $localize`:@@statusLabelPaused:Paused`,
};

// Task statuses are stored as enum-style strings in the DB ("IN_PROGRESS", "OPEN", …).
// Map them directly to the same colors used by TaskStatusConfig in the tasks page.
const TASK_STATUS_MAP: Record<string, PillStatusConfig> = {
  open:         { id: 0, status: StatusData.OPEN.text,         textColor: "#0369a1", color: "#e0f2fe", border: "1px solid #7dd3fc" },
  in_review:    { id: 0, status: StatusData.IN_REVIEW.text,    textColor: "#629D37", color: "#DEEFD2", border: "1px solid #629D37" },
  rejected:     { id: 0, status: StatusData.REJECTED.text,     textColor: "#ffffff", color: "#cc2828", border: "1px solid #cc2828" },
  done:         { id: 0, status: StatusData.DONE.text,         textColor: "#ffffff", color: "#629D37", border: "1px solid #629D37" },
  not_required: { id: 0, status: StatusData.NOT_REQUIRED.text, textColor: "#ffffff", color: "#a0a0a0", border: "1px solid #a0a0a0" },
  in_progress:  { id: 0, status: StatusData.IN_PROGRESS.text,  textColor: "#ffffff", color: "#0984C2", border: "1px solid #0984C2" },
  overdue:      { id: 0, status: StatusData.OVERDUE.text,      textColor: "#ffffff", color: "#ea3f3f", border: "1px solid #ea3f3f" },
};

// app-status-pill is config-driven (statusId -> colors). Search results only carry a
// status string, so we synthesise a single-entry config (id 0) per distinct status,
// coloured by the mapping above. Cached for a stable reference across change detection.
const statusConfigCache = new Map<string, PillStatusConfig[]>();

export function statusConfigFor(status: string): PillStatusConfig[] {
  let config = statusConfigCache.get(status);
  if (!config) {
    const key = status.trim().toLowerCase();
    const taskEntry = TASK_STATUS_MAP[key];
    if (taskEntry) {
      config = [taskEntry];
    } else {
      const family = STATUS_FAMILY[key] ?? "grey";
      const palette = PILL_PALETTE[family];
      const label = STATUS_LABEL[key] ?? status;
      config = [{ id: 0, status: label, textColor: palette.text, color: palette.bg, border: `1px solid ${palette.border}` }];
    }
    statusConfigCache.set(status, config);
  }
  return config;
}
